import useMediaQuery from "@/lib/hooks/use-media-query";
import useScrollableEdges from "@/lib/hooks/use-scrollable-edges";
import { ChevronDown, ChevronUp } from "lucide-react";
import { TenantLogo } from "@/modules/tenants/components/tenant-logo";
import { useTenantsContext } from "@/modules/tenants/tenants.provider";
import { cn } from "@/lib/utils";
import { Slot } from "@radix-ui/react-slot";
import type { ComponentProps, HTMLAttributes } from "react";
import Branding from "../ui/branding/branding";
import Separator from "../ui/separator";
import Sheet, { SheetContent, SheetTitle } from "../ui/sheet/sheet";
import { SIDEBAR_BREAKPOINT, useSidebar } from "./sidebar.provider";

export function Sidebar({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLElement>) {
  const { submenu, open, setOpen, submenuOpen } = useSidebar();
  const matches = useMediaQuery(SIDEBAR_BREAKPOINT);

  // no children means only the submenu shows (settings hides the icon rail)
  const hasRail = Boolean(children);

  const sidebarContent = (
    <>
      {children}
      {submenu && <SidebarSubmenu rail={!hasRail}>{submenu}</SidebarSubmenu>}
    </>
  );

  if (!matches) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="left"
          className={cn(
            "p-0",
            submenu
              ? "w-auto sm:w-auto sm:max-w-none"
              : "w-sidebar-menu sm:w-sidebar-menu sm:max-w-sidebar-menu",
          )}
        >
          <SheetTitle className="sr-only">Sidebar</SheetTitle>
          <aside
            data-slot="sidebar"
            className={cn(
              "grid shrink-0",
              !hasRail
                ? "grid-cols-[auto]"
                : submenu
                  ? "grid-cols-[var(--width-sidebar-menu)_1fr]"
                  : "grid-cols-[var(--width-sidebar-menu)]",
            )}
          >
            {sidebarContent}
          </aside>
        </SheetContent>
      </Sheet>
    );
  }

  // without a rail the single column sizes to the submenu (or collapses to zero)
  const gridCols = !hasRail
    ? "grid-cols-[auto]"
    : submenu && submenuOpen
      ? "grid-cols-[var(--width-sidebar-menu)_1fr]"
      : "grid-cols-[var(--width-sidebar-menu)]";

  return (
    <aside
      data-slot="sidebar"
      className={cn(
        "sticky top-0 bottom-0 left-0 grid max-h-svh shrink-0",
        gridCols,
        className,
      )}
      {...props}
    >
      {sidebarContent}
    </aside>
  );
}

export function SidebarMenu({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLElement>) {
  return (
    <section
      data-slot="sidebar-menu"
      className={cn(
        // header and footer are auto rows; the middle row takes the rest and
        // is the only thing that scrolls
        "bg-sidebar grid h-svh max-h-svh grid-rows-[auto_auto_1fr_auto] gap-4 py-4",
        className,
      )}
      {...props}
    >
      {children}
    </section>
  );
}

export function SidebarMenuHeader({
  className,
  ...props
}: HTMLAttributes<HTMLElement>) {
  return (
    <header
      data-slot="sidebar-menu-header"
      className={cn(
        "flex items-center justify-between justify-self-center",
        className,
      )}
      {...props}
    />
  );
}

export function SidebarMenuLogo({
  className,
  ...props
}: Omit<ComponentProps<typeof TenantLogo>, "logoUrl">) {
  const { tenant } = useTenantsContext();
  const logoUrl = tenant?.brand?.logo_url?.trim();

  if (!logoUrl) {
    return (
      <div
        data-slot="sidebar-menu-logo"
        aria-label="Company logo"
        className={cn("bg-brand size-11 rounded-md shadow", className)}
      />
    );
  }

  return (
    <TenantLogo
      alt="Company logo"
      logoUrl={logoUrl}
      data-slot="sidebar-menu-logo"
      className={cn("size-11 rounded-md", className)}
      {...props}
    />
  );
}

export function SidebarMenuSeparator({
  className,
  ...props
}: ComponentProps<typeof Separator>) {
  return (
    <div className="px-1">
      <Separator
        className={cn("bg-sidebar-on-sidebar/20", className)}
        {...props}
      />
    </div>
  );
}

export function SidebarMenuContent({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLElement>) {
  const { ref, canScrollUp, canScrollDown } = useScrollableEdges<HTMLElement>();

  function scrollByPage(direction: "up" | "down") {
    const element = ref.current;
    if (!element) return;
    const amount = element.clientHeight * 0.8;
    element.scrollBy({
      top: direction === "up" ? -amount : amount,
      behavior: "smooth",
    });
  }

  return (
    <nav
      ref={ref}
      data-slot="sidebar-menu-content"
      className={cn(
        // a plain block scroller, not a flex column: a flex child with
        // overflow-hidden would otherwise shrink to fit and clip the list
        // instead of letting it overflow and scroll
        "relative min-h-0 scroll-py-8 overflow-y-auto overscroll-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        className,
      )}
      {...props}
    >
      <SidebarMenuScrollArrow
        direction="up"
        visible={canScrollUp}
        onClick={() => scrollByPage("up")}
      />
      {children}
      <SidebarMenuScrollArrow
        direction="down"
        visible={canScrollDown}
        onClick={() => scrollByPage("down")}
      />
    </nav>
  );
}

// sits as a zero-height sticky strip so the arrow floats over the list at the
// top or bottom edge without taking up layout space or blocking scroll
function SidebarMenuScrollArrow({
  direction,
  visible,
  onClick,
}: {
  direction: "up" | "down";
  visible: boolean;
  onClick: () => void;
}) {
  const Icon = direction === "up" ? ChevronUp : ChevronDown;
  const label = direction === "up" ? "Scroll up" : "Scroll down";

  return (
    <div
      className={cn(
        "pointer-events-none sticky z-10 h-0",
        direction === "up" ? "top-0" : "bottom-0",
      )}
    >
      <button
        type="button"
        aria-label={label}
        aria-hidden={!visible}
        tabIndex={visible ? 0 : -1}
        onClick={onClick}
        className={cn(
          "interactive bg-foreground text-background pointer-events-auto absolute inset-x-0 mx-auto flex size-6 items-center justify-center rounded-full shadow-md ring-1 ring-black/10 transition-opacity duration-200 hover:opacity-90",
          visible ? "opacity-100" : "pointer-events-none opacity-0",
          direction === "up" ? "top-1.5" : "bottom-1.5",
        )}
      >
        <Icon className="size-3.5" />
      </button>
    </div>
  );
}

export function SidebarMenuFooter({
  className,
  ...props
}: HTMLAttributes<HTMLElement>) {
  return (
    <footer
      data-slot="sidebar-menu-footer"
      className={cn("flex shrink-0 flex-col gap-4 px-2", className)}
      {...props}
    />
  );
}

export function SidebarSubmenu({
  rail = false,
  children,
  className,
  ...props
}: HTMLAttributes<HTMLElement> & {
  /**
   * The submenu is the only sidebar and takes over as the rail: brand
   * color, width set by its content, no branding line
   */
  rail?: boolean;
}) {
  const { submenuOpen } = useSidebar();
  const matches = useMediaQuery(SIDEBAR_BREAKPOINT);

  return (
    <section
      data-slot="sidebar-submenu"
      className={cn(
        "h-svh max-h-svh max-w-full min-w-0 flex-col justify-between gap-4 overflow-hidden",
        rail ? "bg-sidebar" : "w-sidebar-submenu bg-background-sidebar",
        // Above sidebar breakpoint: collapse/expand based on submenuOpen
        // state — except in rail mode, where the submenu is the only
        // navigation left and must never collapse
        matches && !submenuOpen && !rail ? "hidden" : "flex",
        className,
      )}
      {...props}
    >
      <div className="flex min-h-0 grow flex-col gap-1">{children}</div>
      {!rail && <Branding className="pb-3" />}
    </section>
  );
}

export function SidebarSubmenuSeparator(
  props: ComponentProps<typeof Separator>,
) {
  return <Separator className="bg-border" {...props} />;
}

export function SidebarTrigger({
  asChild = false,
  className,
  ...props
}: ComponentProps<"button"> & { asChild?: boolean }) {
  const { open, setOpen, submenu, submenuOpen, setSubmenuOpen } = useSidebar();
  const matches = useMediaQuery(SIDEBAR_BREAKPOINT);

  if (matches && !submenu) {
    return null;
  }

  const Comp = asChild ? Slot : "button";

  function handleClick() {
    if (matches && submenu) {
      setSubmenuOpen(!submenuOpen);
    } else {
      setOpen(!open);
    }
  }

  return (
    <Comp
      onClick={handleClick}
      data-slot="sidebar-trigger"
      className={cn(className)}
      {...props}
    />
  );
}
